import React, { useCallback, useMemo } from "react";
import PerformanceTablePanel from "./PerformanceTablePanel";
import {
  type TableDataRow,
  getBsPerformanceTableColumns,
  mapBsTableRows,
} from "./performanceChartModel";

export interface BsPerformanceTableProps {
  isDark: boolean;
  cardBg: string;
  textSub: string;
  chartBusy: boolean;
  /** Raw BS tableData rows (per year, each metric an { actual, projected } block). */
  rows: any[];
  /** Plan year to highlight. */
  selectedYear: number;
  /** Leading column dimension, driven by the Performance Chart date toggle. */
  leadingDimension?: string;
  /** Active value-metric ids; columns whose metric is not active are grayed out. */
  activeValueFilters?: Set<string>;
  /** Whether projected columns are enabled (otherwise grayed out). */
  showProjections?: boolean;
}

/**
 * Buy Sell performance table. BS `tableData` returns one row per year with each metric as an
 * { actual, projected } block; this flattens them into "<Metric>" / "Projected <Metric>" columns
 * and renders them through the shared PerformanceTablePanel (no grouped header).
 */
const BsPerformanceTable: React.FC<BsPerformanceTableProps> = ({
  isDark,
  cardBg,
  textSub,
  chartBusy,
  rows,
  selectedYear,
  leadingDimension = "planYear",
  activeValueFilters,
  showProjections,
}) => {
  const columns = useMemo(() => getBsPerformanceTableColumns(leadingDimension), [leadingDimension]);
  const tableRows = useMemo(() => mapBsTableRows(rows), [rows]);

  const rowClassName = useCallback(
    (rowData: TableDataRow) => (rowData?.planYear === selectedYear ? "font-semibold" : ""),
    [selectedYear],
  );

  return (
    <PerformanceTablePanel
      isDark={isDark}
      cardBg={cardBg}
      textSub={textSub}
      chartBusy={chartBusy}
      columns={columns}
      rows={tableRows as unknown as TableDataRow[]}
      rowClassName={rowClassName}
      activeValueFilters={activeValueFilters}
      showProjections={showProjections}
      groups={[]}
    />
  );
};

export default BsPerformanceTable;
